import React, { useState, useEffect, useContext } from 'react';
import { fetchPriceHistory } from '../services';
import { formatPrice } from '../processors';
import { GlobalStateContext } from '../GlobalState';
import { BGG_MARKETPLACE_URL, LOADING_MESSAGES } from '../constants';

/**
 * Price History Component
 *
 * A compact pricing report for a game. Shows the average price up front, and
 * can expand to reveal:
 *   - Calculation details (sales count, outliers excluded)
 *   - A recent sales table
 *   - A link to the full price history on BGG
 *
 * @param {string} gameId               - BGG game object ID
 * @param {boolean} [showSalesTable]    - Include detailed sales table when expanded (default: true)
 * @param {boolean} [defaultExpanded]   - Start expanded (default: false)
 * @param {Function} [onAveragePriceLoaded] - Callback with the computed average price
 * @param {Function} [onLoadingChange]  - Callback with the price history loading state
 */
const PriceHistory = ({
  gameId,
  showSalesTable = true,
  defaultExpanded = false,
  onAveragePriceLoaded,
  onLoadingChange,
}) => {
  const { isPriceHistoryExpanded, setIsPriceHistoryExpanded } = useContext(GlobalStateContext);
  const [sales, setSales] = useState([]);
  const [averagePrice, setAveragePrice] = useState(0);
  const [outliers, setOutliers] = useState([]);
  const [items, setItems] = useState([]);
  const [isLoadingPriceHistory, setIsLoadingPriceHistory] = useState(false);

  // Use local expanded state if defaultExpanded is provided, otherwise use global state
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded === true);
  const isExpanded = defaultExpanded !== null ? localExpanded : isPriceHistoryExpanded;
  const setExpanded = defaultExpanded !== null ? setLocalExpanded : setIsPriceHistoryExpanded;

  const toggleExpand = () => {
    setExpanded(!isExpanded);
  };

  const loadData = async (objectId) => {
    setSales([]);
    setOutliers([]);
    setAveragePrice(0);
    setItems([]);
    setIsLoadingPriceHistory(true);
    if (onAveragePriceLoaded) {
      onAveragePriceLoaded(null);
    }
    if (onLoadingChange) {
      onLoadingChange(true);
    }
    try {
      const result = await fetchPriceHistory(objectId);
      setSales(result.sales);
      setOutliers(result.outliers);
      setAveragePrice(result.averagePrice);

      // Sort items by date descending (newest first)
      const sortedItems = [...(result.items || [])].sort((a, b) => {
        return new Date(b.listdate) - new Date(a.listdate);
      });
      setItems(sortedItems);

      if (onAveragePriceLoaded) {
        onAveragePriceLoaded(result.averagePrice);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingPriceHistory(false);
      if (onLoadingChange) {
        onLoadingChange(false);
      }
    }
  };

  useEffect(() => {
    if (gameId) {
      loadData(gameId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  const formattedPrice = averagePrice ? formatPrice(averagePrice) : '0.00';

  return (
    <div className="price-history-container">
      <div className={`price-history-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {isLoadingPriceHistory ? (
          <>
            <div className="price-history-main price-history-main-loading" aria-busy="true">
              <div className="price-history-header-div">
                <span className="price-label">Average Price</span>
                <span className="price-value price-value-placeholder">$—</span>
              </div>

              <div className="price-history-inline-loading">
                <div className="loader" style={{ width: '18px', height: '18px' }}></div>
                <span>{LOADING_MESSAGES.PRICE_HISTORY}</span>
              </div>
            </div>
          </>
        ) : sales.length === 0 ? (
          <div className="price-history-empty">
            <p>No relevant marketplace sales found.</p>
          </div>
        ) : (
          <>
            <div className="price-history-main">
              <div className="price-history-header-div">
                <span className="price-label">Average Price</span>
                <span className="price-value">${formattedPrice}</span>
              </div>

              <button
                className="price-history-toggle"
                onClick={toggleExpand}
                aria-label={isExpanded ? 'Collapse price history' : 'Expand price history'}
              >
                {isExpanded ? (
                  <i className="fa-solid fa-chevron-up"></i>
                ) : (
                  <i className="fa-solid fa-chevron-down"></i>
                )}
              </button>
            </div>

            {isExpanded && (
              <div className="price-history-details">
                <p>
                  Average calculated from last {sales.length}{' '}
                  {sales.length > 1 ? 'sales' : 'sale'}
                </p>
                {outliers.length > 0 && (
                  <p>
                    {outliers.length} {outliers.length > 1 ? 'prices were' : 'price was'} ignored
                    as {outliers.length > 1 ? 'outliers' : 'an outlier'}: $
                    {outliers.map((outlier) => formatPrice(outlier)).join(', $')}
                  </p>
                )}
              </div>
            )}

            {isExpanded && showSalesTable && items.length > 0 && (
              <div className="price-history-sales-table">
                <div className="comments-table-container">
                  <table className="comments-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Price</th>
                        <th>Condition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={index}>
                          <td>{new Date(item.listdate).toLocaleDateString()}</td>
                          <td>${parseFloat(item.price).toFixed(2)}</td>
                          <td>{item.condition || 'Not specified'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isExpanded && (
              <div className="price-history-link">
                <a
                  href={`${BGG_MARKETPLACE_URL}/${gameId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  See full price history on BGG
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PriceHistory;
